<template>
    <main-dialog
        :show="showDialog"
        :title="$t('integrations.support.createTicket')"
        max-width="600px"
        @close="close"
    >
        <!-- Success State -->
        <div v-if="submitted" class="text-center py-3">
            <b-icon icon="check-circle-fill" variant="success" font-scale="3" class="mb-3" />
            <h6 class="font-weight-bold">{{ $t('integrations.support.successTitle') }}</h6>
            <p class="text-muted">{{ $t('integrations.support.successMessage', { number: ticketNumber }) }}</p>
        </div>

        <!-- Form -->
        <div v-else>
            <b-alert v-if="error" variant="danger" show dismissible @dismissed="error = null" class="mb-3">
                {{ error }}
            </b-alert>

            <b-form @submit.prevent="createTicket">
                <b-form-group
                    :label="$t('integrations.support.subject')"
                    label-for="dialog-support-subject"
                    class="mb-3"
                >
                    <b-form-input
                        id="dialog-support-subject"
                        v-model="form.subject"
                        :placeholder="$t('integrations.support.subjectPlaceholder')"
                        required
                        maxlength="200"
                    />
                </b-form-group>

                <b-row>
                    <b-col cols="12" md="6">
                        <b-form-group
                            :label="$t('integrations.support.topic')"
                            label-for="dialog-support-topic"
                            class="mb-3"
                        >
                            <b-form-select
                                id="dialog-support-topic"
                                v-model="form.topic"
                                :options="topicOptions"
                                required
                            />
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="6">
                        <b-form-group
                            :label="$t('integrations.support.priority')"
                            label-for="dialog-support-priority"
                            class="mb-3"
                        >
                            <b-form-select
                                id="dialog-support-priority"
                                v-model="form.priority"
                                :options="priorityOptions"
                                required
                            />
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-form-group
                    :label="$t('integrations.support.description')"
                    label-for="dialog-support-description"
                    class="mb-3"
                >
                    <b-form-textarea
                        id="dialog-support-description"
                        v-model="form.description"
                        :placeholder="$t('integrations.support.descriptionPlaceholder')"
                        rows="5"
                        required
                        maxlength="5000"
                    />
                </b-form-group>

                <div class="d-flex justify-content-end">
                    <b-button variant="outline-secondary" size="sm" class="mr-2" @click="close">
                        {{ $t('integrations.support.cancel') }}
                    </b-button>
                    <b-button
                        type="submit"
                        variant="primary"
                        :disabled="saving"
                        size="sm"
                    >
                        <b-spinner v-if="saving" small class="mr-1" />
                        <b-icon v-else icon="cursor" class="mr-1" />
                        {{ saving ? $t('integrations.support.submitting') : $t('integrations.support.submit') }}
                    </b-button>
                </div>
            </b-form>
        </div>

        <template v-if="submitted" #footer>
            <b-button variant="secondary" size="sm" @click="close">
                {{ $t('close') }}
            </b-button>
        </template>
    </main-dialog>
</template>

<script>
var TOPICS_BY_TIER = {
    free: ['general'],
    standard: ['general', 'technical'],
    pro: ['general', 'technical', 'billing', 'account', 'priority_support']
}

var PRIORITIES_BY_TIER = {
    free: ['normal'],
    standard: ['low', 'normal', 'high'],
    pro: ['low', 'normal', 'high', 'urgent']
}

export default {
    name: 'CreateTicketDialog',

    data: function() {
        return {
            showDialog: false,
            tier: 'free',
            form: {
                subject: '',
                description: '',
                topic: 'general',
                priority: 'normal'
            },
            saving: false,
            submitted: false,
            ticketNumber: null,
            error: null
        }
    },

    computed: {
        topicOptions: function() {
            var allowed = TOPICS_BY_TIER[this.tier] || ['general']
            var self = this
            return allowed.map(function(t) {
                return { value: t, text: self.$t('integrations.support.topics.' + t) }
            })
        },

        priorityOptions: function() {
            var allowed = PRIORITIES_BY_TIER[this.tier] || ['normal']
            var self = this
            return allowed.map(function(p) {
                return { value: p, text: self.$t('integrations.support.priorities.' + p) }
            })
        }
    },

    mounted: function() {
        var self = this
        this.$root.$on('open-support-ticket-dialog', function(options) {
            self.open(options)
        })
    },

    beforeDestroy: function() {
        this.$root.$off('open-support-ticket-dialog')
    },

    methods: {
        open: function(options) {
            this.showDialog = true
            this.submitted = false
            this.error = null
            this.saving = false
            this.ticketNumber = null
            this.form = {
                subject: (options && options.subject) ? options.subject : '',
                description: (options && options.description) ? options.description : '',
                topic: 'general',
                priority: 'normal'
            }
            this.loadTierInfo()
        },

        close: function() {
            this.showDialog = false
        },

        loadTierInfo: function() {
            var self = this
            self.$integrationApi.get('/protokol-support/support/info').then(function(resp) {
                if (resp.data && resp.data.tier) {
                    self.tier = resp.data.tier
                }
                var allowed = TOPICS_BY_TIER[self.tier] || ['general']
                if (allowed.indexOf(self.form.topic) === -1) {
                    self.form.topic = allowed[0]
                }
            }).catch(function() {
                // defaults to free tier
            })
        },

        createTicket: function() {
            var self = this
            self.saving = true
            self.error = null

            self.$integrationApi.post('/protokol-support/support/tickets', {
                subject: self.form.subject,
                description: self.form.description,
                topic: self.form.topic,
                priority: self.form.priority
            }).then(function(resp) {
                self.saving = false
                self.submitted = true
                self.ticketNumber = (resp.data && resp.data.ticket_number) ? resp.data.ticket_number : null
            }).catch(function(err) {
                self.saving = false
                var code = err.response && err.response.data && err.response.data.code
                if (code === 4029) {
                    self.error = self.$t('integrations.support.limitExceeded')
                } else if (err.response && err.response.data && err.response.data.message) {
                    self.error = err.response.data.message
                } else {
                    self.error = self.$t('integrations.support.errorGeneric')
                }
            })
        }
    }
}
</script>
